import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function TermsOfService() {
  const s = useSiteSettings();
  const name = s.business_name || "Gil Maakaroun";
  const jurisdiction = s.legal_jurisdiction || "the Commonwealth of Massachusetts, United States";
  const email = s.contact_email || "";
  const independenceDisclaimer =
    s.disclaimer_footer ||
    `${name}'s album-completion program is independently operated and is not affiliated with, endorsed by, or approved by Panini, FIFA, or any team, league, or sports federation. All certificates are independently inspected and recorded by ${name}.`;
  return (
    <LegalLayout
      title="Terms of Service"
      seoTitle="Terms of Service"
      seoDescription={`The rules for using the ${name} collector platform.`}
      intro="The rules for using this site and our services. Written plainly so nine-year-olds and their parents can understand them together."
      effectiveDate="February 2026"
      testid="tos-page"
    >
      <h2>1. Who this covers</h2>
      <p>
        These Terms of Service (&quot;Terms&quot;) form an agreement between you
        and {name} (&quot;we,&quot; &quot;us,&quot; or the
        &quot;Company&quot;) when you use this website, the connected
        Shopify storefront, or any related service.
      </p>

      <h2>2. Independence disclaimer</h2>
      <p data-testid="tos-independence-disclaimer">{independenceDisclaimer}</p>

      <h2>3. What we offer</h2>
      <ul>
        <li><strong>Shop</strong> — albums, packs, boxes, Gold Crumples, accessories, sets, and merchandise, fulfilled through our Shopify storefront.</li>
        <li><strong>Missing Sticker Requests</strong> — a manual, human-reviewed sourcing service. You submit a list; we confirm availability and price; you approve; we invoice; we ship or hand off.</li>
        <li><strong>Events</strong> — free RSVP for local trading events and community meetups.</li>
        <li><strong>Certificates</strong> — numbered, permanent public records of albums we have independently inspected.</li>
      </ul>

      <h2>4. Missing sticker requests</h2>
      <ul>
        <li>Submitting a list is a <strong>request</strong>, not a purchase. Nothing is charged until you approve a quote.</li>
        <li>We do not maintain live inventory for every individual sticker. Availability is confirmed manually.</li>
        <li>Prices in quotes are valid for the window stated on the quote.</li>
      </ul>

      <h2>5. Events</h2>
      <ul>
        <li>RSVPs are free unless otherwise stated on the event page.</li>
        <li>Capacity may be limited; RSVPs are honored first-come first-served.</li>
        <li>Attendees are expected to behave respectfully. We reserve the right to decline attendance for behavior that is unsafe, harassing, or disruptive.</li>
      </ul>

      <h2>6. Certificate program</h2>
      <ul>
        <li>Certificates are issued only after a physical inspection by {name} or an authorized reviewer.</li>
        <li>Numbers are permanent. If a certificate must be corrected, we <em>replace</em> or <em>void</em> — we never silently rewrite history.</li>
        <li>Public visibility (full name / first name + initial / hidden) is your choice at the time of registration.</li>
      </ul>

      <h2>7. Payments, shipping, taxes</h2>
      <p>
        Purchases in the Shopify storefront are governed by Shopify&apos;s
        checkout, tax, and payment rules. Shipping estimates and taxes are
        displayed at checkout. See our <a href="/shipping-policy">Shipping Policy</a> and <a href="/return-policy">Return Policy</a>.
      </p>

      <h2>8. Acceptable use</h2>
      <ul>
        <li>Do not attempt to compromise, scrape, or overload the site.</li>
        <li>Do not impersonate another collector when submitting forms.</li>
        <li>Do not upload content that infringes anyone&apos;s rights.</li>
      </ul>

      <h2>9. Intellectual property</h2>
      <p>
        The {name} brand, our original photography, illustrations, and
        the Certificate Registry design belong to us. Panini, FIFA, and other
        third-party marks belong to their respective owners; we reference them
        only as needed to describe the products we source.
      </p>

      <h2>10. Warranties and liability</h2>
      <p>
        The service is provided &quot;as is.&quot; To the fullest extent allowed
        by law, we are not liable for indirect or consequential losses. For any
        purchase, our liability is limited to the amount you paid for that
        purchase.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These Terms are governed by the laws of {jurisdiction} without regard
        to conflict-of-law rules. Disputes go to the state or federal courts
        located in that jurisdiction.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about these Terms? {email ? (
          <>Reach us at <a href={`mailto:${email}`}>{email}</a> or via the <a href="/contact">Contact page</a>.</>
        ) : (
          <>Reach us via the <a href="/contact">Contact page</a>.</>
        )}
      </p>
    </LegalLayout>
  );
}
